import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, Tabs, Tab, TextField, Select, MenuItem, Button, CircularProgress } from '@mui/material';
import { Close as CloseIcon, AutoAwesome as AIIcon, Search as SearchIcon, Save as SaveIcon, KeyboardArrowDown as ArrowDownIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { runAIVariables, saveAIVariableKeys, getAIVariableKeyStatus } from '../../../store/Campaign';

// ─── Constants ────────────────────────────────────────────────────────────────

const MODELS = [
  { id: 'gpt-4o',           label: 'GPT-4o',           credits: 1, dot: '#10B981' },
  { id: 'gpt-4o-mini',      label: 'GPT-4o Mini',      credits: 1, dot: '#10B981' },
  { id: 'claude-3.5-sonnet',label: 'Claude 3.5 Sonnet', credits: 2, dot: '#8B5CF6' },
  { id: 'deepseek-v3',      label: 'DeepSeek V3',      credits: 1, dot: '#3B82F6' },
];

const VARIABLES = [
  '{{first_name}}', '{{last_name}}', '{{company_name}}', '{{job_title}}',
  '{{industry}}', '{{linkedin_url}}', '{{website}}', '{{city}}', '{{email}}',
];

const TEMPLATES = [
  { title: 'Personalized Icebreaker', description: 'Unique one-liner based on LinkedIn + company website.', category: 'Copywriting', credits: 2, runs: '3.2M', prompt: 'Write a unique, 1-sentence icebreaker for {{first_name}} at {{company_name}} based on their LinkedIn {{linkedin_url}}. Under 20 words, conversational.' },
  { title: 'Company Pain Point', description: 'Top pain point based on industry and company size.', category: 'Research', credits: 2, runs: '1.8M', prompt: 'What is the top pain point for a company like {{company_name}} in the {{industry}} industry? One sentence, specific.' },
  { title: 'Company Name Cleanup', description: 'Remove LLC/Inc/Corp for natural outreach copy.', category: 'Cleaning', credits: 1, runs: '6.1M', prompt: 'Remove LLC, Inc, Corp, Ltd and similar suffixes from this company name and return only the clean name: {{company_name}}' },
  { title: 'Job Title Normalizer', description: 'Normalize titles for consistent segmentation.', category: 'Cleaning', credits: 1, runs: '5.4M', prompt: 'Normalize this job title for segmentation (e.g. "Sr. VP of Sales" → "VP of Sales"): {{job_title}}' },
  { title: 'Compliment Generator', description: 'Short, genuine company compliment as a warm opener.', category: 'Copywriting', credits: 2, runs: '2.0M', prompt: 'Write a short, genuine 1-sentence compliment about {{company_name}} that could be used as a warm opener in a cold email. Be specific, not generic.' },
];

const CATEGORY_COLORS = {
  Copywriting: { bg: '#EDE9FE', color: '#7C3AED' },
  Research:    { bg: '#DBEAFE', color: '#1D4ED8' },
  Cleaning:    { bg: '#D1FAE5', color: '#065F46' },
};

function getInitials(contact) {
  const f = contact?.firstName?.[0] || '';
  const l = contact?.lastName?.[0] || '';
  return (f + l).toUpperCase() || contact?.email?.[0]?.toUpperCase() || '?';
}

const AVATAR_COLORS = ['#6366F1', '#EC4899', '#F59E0B', '#10B981', '#3B82F6'];

// ─── Component ────────────────────────────────────────────────────────────────

const AIVariablesDrawer = ({ open, onClose, contacts = [], onColumnGenerated, campaignId }) => {
  const dispatch = useDispatch();
  const { aiVariablesLoading, aiVariablesColumns, aiVariableKeyStatus } = useSelector(s => s['Campaign']);
  const creditsBalance = Math.floor(useSelector(s => s['Settings']?.['currentSubscription']?.['creditsBalance']) || 0);

  const [tab, setTab] = useState(0);
  const [columnName, setColumnName] = useState('');
  const [editingColumn, setEditingColumn] = useState(false);
  const [model, setModel] = useState('gpt-4o');
  const [prompt, setPrompt] = useState('');
  const [showVars, setShowVars] = useState(false);
  const [previewContact, setPreviewContact] = useState(null);
  const [previewText, setPreviewText] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [apiKeys, setApiKeys] = useState({ openai: '', anthropic: '', xai: '', deepseek: '' });
  const [savingKeys, setSavingKeys] = useState(false);
  const [showRunMenu, setShowRunMenu] = useState(false);
  const [enrichFirst, setEnrichFirst] = useState(1);

  const previewContacts = contacts.slice(0, 5);
  const totalContacts = contacts.length;
  const selectedModel = MODELS.find(m => m.id === model) || MODELS[0];
  const creditEstimate = selectedModel.credits * totalContacts;

  useEffect(() => {
    if (open) {
      dispatch(getAIVariableKeyStatus());
      if (previewContacts.length > 0 && !previewContact) setPreviewContact(previewContacts[0]);
    }
  }, [open]);

  useEffect(() => {
    if (previewContacts.length > 0 && !previewContact) setPreviewContact(previewContacts[0]);
  }, [contacts]);

  // Sync preview text when column result comes in for selected contact
  useEffect(() => {
    if (columnName && aiVariablesColumns[columnName] && previewContact) {
      const value = aiVariablesColumns[columnName][previewContact._id];
      if (value !== undefined) setPreviewText(value);
    }
  }, [aiVariablesColumns, columnName, previewContact]);

  const insertVariable = (v) => {
    setPrompt(p => p + v);
    setShowVars(false);
  };

  const handleTestPreview = () => {
    if (!prompt || !previewContact) return;
    dispatch(runAIVariables({ contacts: [previewContact], prompt, model, columnName: '__preview__', campaignId,
      successCallback: (body) => {
        const val = body.results?.[0]?.value || '';
        setPreviewText(val);
      },
    }));
  };

  const handleRunAll = () => {
    if (!prompt || !columnName) return;
    dispatch(runAIVariables({ contacts, prompt, model, columnName, campaignId,
      successCallback: (body) => {
        if (onColumnGenerated) onColumnGenerated({ columnName, results: body.results });
      },
    }));
  };

  const handleRunN = (n) => {
    setShowRunMenu(false);
    if (!prompt) { toast.error('Please enter a prompt first.'); return; }
    if (!columnName) { toast.error('Please enter an Output Column name first.'); return; }
    const subset = n === 'all' ? contacts : contacts.slice(0, n);
    const creditsNeeded = selectedModel.credits * subset.length;
    if (creditsBalance < creditsNeeded) {
      const maxContacts = Math.floor(creditsBalance / selectedModel.credits);
      toast.error(`Not enough credits. Need ${creditsNeeded}, have ${creditsBalance}. Select ${maxContacts} or fewer contacts.`);
      return;
    }
    dispatch(runAIVariables({ contacts: subset, prompt, model, columnName, campaignId,
      successCallback: (body) => {
        if (onColumnGenerated) onColumnGenerated({ columnName, results: body.results });
      },
    }));
  };

  const handleSaveKeys = () => {
    setSavingKeys(true);
    dispatch(saveAIVariableKeys({ ...apiKeys, successCallback: () => setSavingKeys(false),
      failCallback: () => setSavingKeys(false),
    }));
  };

  const handleUseTemplate = (t) => {
    setPrompt(t.prompt);
    setColumnName(t.title);
    setEditingColumn(false);
    setTab(0);
  };

  const filteredTemplates = TEMPLATES.filter(t =>
    t.title.toLowerCase().includes(templateSearch.toLowerCase()) ||
    t.description.toLowerCase().includes(templateSearch.toLowerCase())
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose} className="aiv-drawer">
      <div className="aiv-drawer-content">

        {/* Header */}
        <div className="aiv-header">
          <div className="aiv-header-title">
            <div className="aiv-header-icon">
              <AIIcon style={{ fontSize: 18, color: 'var(--primary-color)' }} />
            </div>
            <div>
              <div className="aiv-title">AI Variables</div>
              <div className="aiv-subtitle">Enrich leads with AI-generated columns</div>
            </div>
          </div>
          <button className="aiv-close" onClick={onClose}><CloseIcon style={{ fontSize: 18 }} /></button>
        </div>

        {/* Tabs */}
        <div className="aiv-tabs">
          <Tabs value={tab} onChange={(_, v) => setTab(v)} className="aiv-tabs-inner">
            <Tab label="Build Prompt" disableRipple />
            <Tab label="Templates" disableRipple />
            <Tab label="Settings" disableRipple />
          </Tabs>
        </div>

        {/* Body */}
        <div className="aiv-body">

          {/* ── Tab 0: Build Prompt ── */}
          {tab === 0 && (
            <div className="aiv-build">

              {/* Output Column */}
              <div className="aiv-field">
                <label className="aiv-label">Output Column <span className="aiv-required">*</span></label>
                {editingColumn ? (
                  <div className="aiv-column-input-row">
                    <TextField
                      size="small" fullWidth autoFocus
                      value={columnName}
                      onChange={e => setColumnName(e.target.value)}
                      placeholder="e.g. icebreaker"
                      onKeyDown={e => { if (e.key === 'Enter') setEditingColumn(false); if (e.key === 'Escape') setEditingColumn(false); }}
                    />
                    <Button size="small" variant="contained" onClick={() => setEditingColumn(false)} disabled={!columnName}>Save</Button>
                    <button className="aiv-column-cancel" onClick={() => { setEditingColumn(false); setColumnName(''); }}><CloseIcon style={{ fontSize: 14 }} /></button>
                  </div>
                ) : (
                  <button className="aiv-column-placeholder" onClick={() => setEditingColumn(true)}>
                    {columnName ? <><span className="aiv-column-name">{columnName}</span></> : <>+ Create New Column</>}
                  </button>
                )}
              </div>

              {/* AI Model */}
              <div className="aiv-field">
                <label className="aiv-label">AI Model <span className="aiv-required">*</span></label>
                <Select value={model} onChange={e => setModel(e.target.value)} size="small" fullWidth className="aiv-model-select">
                  {MODELS.map(m => (
                    <MenuItem key={m.id} value={m.id}>
                      <span className="aiv-model-dot" style={{ background: m.dot }} />
                      <span className="aiv-model-label">{m.label}</span>
                      <span className="aiv-model-credit">⚡ {m.credits}</span>
                    </MenuItem>
                  ))}
                </Select>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
                  <span style={{ fontSize: 12, color: '#6B7280' }}>⚡ <strong>{creditsBalance.toLocaleString()}</strong> credits available</span>
                </div>
              </div>

              {/* Prompt */}
              <div className="aiv-field">
                <div className="aiv-label-row">
                  <label className="aiv-label">Prompt <span className="aiv-required">*</span></label>
                  <button className="aiv-insert-var-btn" onClick={() => setShowVars(v => !v)}>
                    {'{{ Insert Variable }}'}
                  </button>
                </div>
                <TextField
                  multiline minRows={6} fullWidth size="small"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder={"Write a unique, 1-sentence icebreaker for {{first_name}} at {{company_name}}...\n\nTip: Use {{first_name}}, {{company_name}}, {{job_title}} and more to personalize each row."}
                  className="aiv-prompt-field"
                />
                {showVars && (
                  <div className="aiv-vars-row">
                    <div className="aiv-vars-label">AVAILABLE VARIABLES</div>
                    <div className="aiv-vars-chips">
                      {VARIABLES.map(v => (
                        <button key={v} className="aiv-var-chip" onClick={() => insertVariable(v)}>{v}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Preview For */}
              {previewContacts.length > 0 && (
                <div className="aiv-field">
                  <label className="aiv-label">Preview For</label>
                  <div className="aiv-avatar-row">
                    {previewContacts.map((c, i) => {
                      const isSelected = previewContact?._id === c._id;
                      const name = `${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email || '?';
                      const shortName = c.firstName || name.split(' ')[0] || '?';
                      return (
                        <button
                          key={c._id}
                          className={`aiv-avatar-chip${isSelected ? ' selected' : ''}`}
                          style={{ '--chip-color': AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                          onClick={() => setPreviewContact(c)}
                        >
                          <span className="aiv-avatar-chip-dot" style={{ background: isSelected ? '#fff' : AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                            {getInitials(c)}
                          </span>
                          <span className="aiv-avatar-chip-name">{shortName}</span>
                        </button>
                      );
                    })}
                  </div>

                  {previewContact && (
                    <div className="aiv-preview-box">
                      <div className="aiv-preview-label">
                        PREVIEW — {`${previewContact.firstName || ''} ${previewContact.lastName || ''}`.trim().toUpperCase() || previewContact.email?.toUpperCase()}
                      </div>
                      <div className="aiv-preview-text">
                        {aiVariablesLoading
                          ? <span className="aiv-preview-loading"><CircularProgress size={14} /> Generating...</span>
                          : previewText || <span className="aiv-preview-empty">Click "Test Preview" to see a sample output</span>
                        }
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Credit Estimate */}
              <div className="aiv-credit-estimate">
                ⚡ <strong>Credit estimate:</strong> {creditEstimate} credits for {totalContacts} contacts
              </div>
            </div>
          )}

          {/* ── Tab 1: Templates ── */}
          {tab === 1 && (
            <div className="aiv-templates">
              <div className="aiv-search-box">
                <SearchIcon style={{ fontSize: 16, color: '#9CA3AF' }} />
                <input
                  className="aiv-search-input"
                  placeholder="Search templates..."
                  value={templateSearch}
                  onChange={e => setTemplateSearch(e.target.value)}
                />
              </div>
              <div className="aiv-template-list">
                {filteredTemplates.map((t, i) => (
                  <div key={i} className="aiv-template-card">
                    <div className="aiv-template-top">
                      <div className="aiv-template-info">
                        <div className="aiv-template-title">{t.title}</div>
                        <div className="aiv-template-desc">{t.description}</div>
                        <div className="aiv-template-meta">
                          <span className="aiv-template-runs">🔥 {t.runs} runs</span>
                          <span className="aiv-template-cat" style={{ background: CATEGORY_COLORS[t.category]?.bg, color: CATEGORY_COLORS[t.category]?.color }}>{t.category}</span>
                        </div>
                      </div>
                      <div className="aiv-template-right">
                        <span className="aiv-template-credit">⚡ {t.credits}</span>
                        <Button size="small" variant="contained" className="aiv-use-btn" onClick={() => handleUseTemplate(t)}>Use →</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Tab 2: Settings ── */}
          {tab === 2 && (
            <div className="aiv-settings">
              <div className="aiv-settings-banner">
                ℹ Connect your own API keys to bypass SellAgent credits entirely. Leave blank to use platform credits automatically.
              </div>
              {[
                { key: 'openai',    label: 'OpenAI',           placeholder: 'sk-', color: '#10B981' },
                { key: 'anthropic', label: 'Anthropic (Claude)', placeholder: 'sk-ant-', color: '#8B5CF6' },
                { key: 'xai',       label: 'xAI (Grok)',        placeholder: 'xai-', color: '#374151' },
                { key: 'deepseek',  label: 'DeepSeek',          placeholder: 'sk-', color: '#3B82F6' },
              ].map(({ key, label, placeholder, color }) => (
                <div key={key} className="aiv-key-field">
                  <div className="aiv-key-label-row">
                    <span className="aiv-key-dot" style={{ background: color }} />
                    <label className="aiv-key-label">{label}</label>
                    {aiVariableKeyStatus[key] && <span className="aiv-key-saved">Key saved ✓</span>}
                  </div>
                  <TextField
                    fullWidth size="small" type="password"
                    value={apiKeys[key]}
                    onChange={e => setApiKeys(k => ({ ...k, [key]: e.target.value }))}
                    placeholder={aiVariableKeyStatus[key] ? '••••••••••••••••' : placeholder}
                  />
                </div>
              ))}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="aiv-footer">
          {tab === 0 && (
            <>
              <Button variant="outlined" size="small" className="aiv-test-btn" onClick={handleTestPreview} disabled={!prompt || aiVariablesLoading}>
                {aiVariablesLoading ? <CircularProgress size={14} style={{ marginRight: 6 }} /> : null}
                ▶ Test Preview
              </Button>
              <div className="aiv-run-btn-wrap">
                <Button
                  variant="contained" size="small"
                  className="aiv-run-btn"
                  onClick={() => handleRunN('all')}
                  disabled={!prompt || aiVariablesLoading}
                >
                  {aiVariablesLoading ? <CircularProgress size={14} style={{ marginRight: 6, color: '#fff' }} /> : null}
                  ✦ Run All ({totalContacts} rows)
                </Button>
                <Button
                  variant="contained" size="small"
                  className="aiv-run-arrow-btn"
                  onClick={() => setShowRunMenu(v => !v)}
                  disabled={aiVariablesLoading}
                >
                  <ArrowDownIcon style={{ fontSize: 16 }} />
                </Button>
                {showRunMenu && (
                  <div className="aiv-run-menu" style={{ position: 'absolute', bottom: '110%', right: 0, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 260, zIndex: 999, overflow: 'hidden' }}>
                    {/* Option 1: Enrich first N */}
                    <div className="aiv-run-menu-item" style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#111827', whiteSpace: 'nowrap' }}>Enrich first</span>
                      <input
                        type="number" min={1} max={totalContacts}
                        value={enrichFirst}
                        onClick={e => e.stopPropagation()}
                        onChange={e => setEnrichFirst(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{ width: 52, border: '1px solid #E5E7EB', borderRadius: 6, padding: '3px 6px', fontSize: 13, textAlign: 'center', outline: 'none' }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>leads</span>
                      <span
                        onClick={() => handleRunN(enrichFirst)}
                        style={{ marginLeft: 'auto', background: '#F3F4F6', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        ~{enrichFirst} total
                      </span>
                    </div>
                    {/* Option 2: Enrich 100 */}
                    <div
                      className="aiv-run-menu-item"
                      onClick={() => handleRunN(100)}
                      style={{ padding: '14px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>Enrich 100 leads</span>
                      <span style={{ background: '#F3F4F6', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600, color: '#374151' }}>~100 total</span>
                    </div>
                    {/* Option 3: Enrich all */}
                    <div
                      className="aiv-run-menu-item"
                      onClick={() => handleRunN('all')}
                      style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>Enrich all leads in the list</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          {tab === 2 && (
            <Button variant="contained" fullWidth className="aiv-save-keys-btn" onClick={handleSaveKeys} disabled={savingKeys} startIcon={<SaveIcon />}>
              {savingKeys ? 'Saving...' : 'Save API Keys'}
            </Button>
          )}
        </div>

      </div>
    </Drawer>
  );
};

export default AIVariablesDrawer;
